import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";
import { Icons } from "@/components/ui/icons";
import { OnboardingCard } from "./onboarding-card";
import { isTauri } from "@/lib/is-tauri";
import { useAuth } from "@/hooks/use-auth";
import { useT } from "@/i18n";
import { firstRunMessages } from "@/i18n/messages/first-run";
import { commonMessages } from "@/i18n/messages/common";

interface Props {
  onContinue: (name: string) => void;
  onSkip: () => void;
}

function deriveSuggestedName(
  user: { name?: string; email?: string } | null,
  osAccountName: string,
): string {
  const clean = (value: string | null | undefined) =>
    (value ?? "").trim().replace(/\s+/g, " ");

  const osName = clean(osAccountName);
  if (osName) return osName.split(/\s+/)[0] ?? osName;

  const name = clean(user?.name);
  if (name && !/^test user$/i.test(name)) return name.split(/\s+/)[0] ?? name;

  const local = clean(user?.email?.split("@")[0]);
  if (!local || /^(test|demo|admin|user)$/i.test(local)) return "";
  const first = local.split(/[._+-]/).filter(Boolean)[0] ?? "";
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "";
}

export function OnboardingWelcomeStep({ onContinue, onSkip }: Props) {
  const t = useT(firstRunMessages);
  const tc = useT(commonMessages);
  const { user } = useAuth();
  const [osAccountName, setOsAccountName] = useState("");
  const [name, setName] = useState("");
  const [initialized, setInitialized] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    void import("@/desktop/onboarding/use-os-name").then(
      ({ getOsAccountName }) =>
        getOsAccountName().then((n) => {
          if (!cancelled) setOsAccountName(n);
        }),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (initialized) return;
    const suggested = deriveSuggestedName(user, osAccountName);
    if (suggested) {
      setName(suggested);
      setInitialized(true);
    } else if (!isTauri() && user) {
      setInitialized(true);
    } else if (isTauri() && osAccountName !== "") {
      setInitialized(true);
    }
  }, [user, osAccountName, initialized]);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  return (
    <OnboardingCard data-testid="onboarding-welcome" withBackdrop>
      <div className="flex flex-col items-center gap-1">
        <div className="flex h-12 w-12 items-center justify-center rounded-panel border">
          <Icons.Sparkles className="h-6 w-6 text-accent" />
        </div>
        <h1 className="mt-2 text-appkit-title font-semibold text-[var(--agent-aurora-text)]">
          {t("webWelcomeTitle")}
        </h1>
        <p className="text-center text-appkit-body text-[var(--agent-aurora-text-muted)]">
          {t("webWelcomeAsk")}
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onContinue(name.trim());
        }}
      >
        <TextInput
          ref={inputRef}
          data-testid="onboarding-name-input"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder={t("yourName")}
          maxLength={100}
          className="h-10 text-appkit-body"
          autoComplete="name"
        />
        <Button
          type="submit"
          data-testid="onboarding-welcome-continue"
          size="lg"
          disabled={!name.trim()}
        >
          {tc("continue")}
        </Button>
      </form>

      <Button
        type="button"
        data-testid="onboarding-welcome-skip"
        onClick={onSkip}
        variant="ghost"
        size="sm"
        className="self-center text-[var(--agent-aurora-text-muted)] hover:text-[var(--agent-aurora-text)]"
      >
        {t("skipSetup")}
      </Button>
    </OnboardingCard>
  );
}
